/**
 * Debug page that opens a raw WS connection to a room and drives it manually.
 * No video player attached — just buttons for play/pause/seek and a live
 * readout of `room_state`, `schedule_event`, drift estimates from heartbeats,
 * and connected member count.
 *
 * Use this to validate sync without the complexity of a real player.
 */
import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { WsClient } from '../ws/client.ts';

interface LogEntry {
  ts: number;
  text: string;
}

export function DebugRoomPage() {
  const { id } = useParams<{ id: string }>();
  const wsRef = useRef<WsClient | null>(null);
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [position, setPosition] = useState(0);
  const [seekTarget, setSeekTarget] = useState(0);

  useEffect(() => {
    if (!id) return;
    const wsProtocol = window.location.protocol === 'https:' ? 'wss' : 'ws';
    const ws = new WsClient({
      url: `${wsProtocol}://${window.location.host}/ws/room/${id}`,
      onMessage: (msg) => {
        setLogs((l) => [{ ts: Date.now(), text: JSON.stringify(msg) }, ...l.slice(0, 99)]);
        if (msg.type === 'room_state') setPosition(msg.position);
      },
    });
    wsRef.current = ws;
    ws.connect();
    const t = setInterval(() => {
      ws.send({ type: 'heartbeat', client_time: Date.now(), client_position: position });
    }, 2000);
    return () => {
      clearInterval(t);
      ws.close();
      wsRef.current = null;
    };
  }, [id, position]);

  return (
    <div className="p-4 max-w-3xl mx-auto">
      <h1 className="text-lg font-semibold mb-3">debug room {id}</h1>
      <div className="flex flex-wrap gap-2 mb-4">
        <button
          type="button"
          onClick={() => wsRef.current?.send({ type: 'play', at_position: position })}
          className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 rounded text-sm"
        >
          play
        </button>
        <button
          type="button"
          onClick={() => wsRef.current?.send({ type: 'pause', at_position: position })}
          className="px-3 py-1.5 bg-zinc-700 hover:bg-zinc-600 rounded text-sm"
        >
          pause
        </button>
        <input
          type="number"
          value={seekTarget}
          onChange={(e) => setSeekTarget(Number.parseFloat(e.target.value))}
          className="w-24 px-2 py-1 bg-zinc-800 rounded text-sm"
          aria-label="Seek target"
        />
        <button
          type="button"
          onClick={() => wsRef.current?.send({ type: 'seek', target_position: seekTarget })}
          className="px-3 py-1.5 bg-zinc-700 hover:bg-zinc-600 rounded text-sm"
        >
          seek
        </button>
        <button
          type="button"
          onClick={() => wsRef.current?.send({ type: 'ready', reason: 'join' })}
          className="px-3 py-1.5 bg-zinc-700 hover:bg-zinc-600 rounded text-sm"
        >
          ready
        </button>
      </div>
      <div className="text-xs text-zinc-400 mb-2">
        position: {position.toFixed(2)} · clock offset:{' '}
        {wsRef.current?.getClockOffset?.()?.toFixed(0) ?? 0}ms
      </div>
      <ul className="font-mono text-xs space-y-1 bg-zinc-900 p-3 rounded max-h-[60vh] overflow-y-auto">
        {logs.map((l) => (
          <li key={l.ts} className="text-zinc-300">
            <span className="text-zinc-600">[{new Date(l.ts).toLocaleTimeString()}]</span> {l.text}
          </li>
        ))}
      </ul>
    </div>
  );
}
